import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import axios from 'axios';
import { Box, Typography, Skeleton } from '@mui/material';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function DashGraphs({ productId, productTitle, yMax, yStep, daysBack = 30, inventoryData: inventoryDataProp, onInventoryLoaded }) {
  const hasProvidedData = inventoryDataProp !== undefined;
  const [inventoryData, setInventoryData] = useState(hasProvidedData ? inventoryDataProp : []);
  const [loaded, setLoaded] = useState(hasProvidedData);
  const navigate = useNavigate();

  useEffect(() => {
    // When the parent supplies inventory (batch fetch on the graphs page),
    // use it directly instead of making a per-graph request.
    if (hasProvidedData) {
      setInventoryData(inventoryDataProp);
      setLoaded(true);
      if (onInventoryLoaded) {
        const latest = inventoryDataProp.length
          ? Number(inventoryDataProp[inventoryDataProp.length - 1].quantity ?? inventoryDataProp[inventoryDataProp.length - 1].qty ?? 0)
          : null;
        onInventoryLoaded(productId, latest);
      }
      return;
    }

    if (!productId) return;
    setLoaded(false);
    axios
      .get(`https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/inventory/${productId}`)
      .then(response => {
        const data = response.data || [];
        setInventoryData(data);
        if (onInventoryLoaded) {
          const latest = data.length
            ? Number(data[data.length - 1].quantity ?? data[data.length - 1].qty ?? 0)
            : null;
          onInventoryLoaded(productId, latest);
        }
      })
      .catch(error => {
        console.error('Error fetching inventory data:', error);
        if (onInventoryLoaded) onInventoryLoaded(productId, null);
      })
      .finally(() => setLoaded(true));
  }, [productId, hasProvidedData, inventoryDataProp, onInventoryLoaded]);

  const { labels, values } = useMemo(() => {
    if (!inventoryData || inventoryData.length === 0) {
      return { labels: [], values: [] };
    }

    const withParsedDates = inventoryData.map((entry, idx) => {
      const raw =
        entry.date ||
        entry.createdAt ||
        entry.timestamp ||
        entry.updatedAt ||
        entry.time;
      const parsed = raw ? new Date(raw) : null;
      return {
        ...entry,
        __idx: idx,
        __date: parsed && !isNaN(parsed) ? parsed : null,
      };
    });

    let filtered;
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - (daysBack - 1));
    start.setHours(0, 0, 0, 0);

    if (withParsedDates.some(e => e.__date)) {
      filtered = withParsedDates
        .filter(e => e.__date && e.__date >= start && e.__date <= now)
        .sort((a, b) => a.__date - b.__date);

      if (filtered.length === 0) {
        filtered = withParsedDates.slice(-daysBack);
      }
    } else {
      filtered = withParsedDates.slice(-daysBack);
    }

    const lbls = filtered.map((e, i) => {
      if (e.__date) {
        return e.__date.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
      }
      return `Data ${i + 1}`;
    });

    // Kept unclamped: a negative quantity means oversold/backordered, which is
    // real signal. Flooring it at 0 made those products indistinguishable from
    // genuinely empty ones.
    const vals = filtered.map(e => Number(e.quantity ?? e.qty ?? 0));

    return { labels: lbls, values: vals };
  }, [inventoryData, daysBack]);

  const computedYMax = useMemo(() => {
    if (yMax !== undefined) return yMax;
    if (!values.length) return 10;
    const peak = Math.max(...values, 0);
    if (peak <= 10) return 10;
    const order = Math.pow(10, Math.floor(Math.log10(peak)));
    return Math.ceil((peak * 1.15) / order) * order;
  }, [values, yMax]);

  // Only dips below zero when the data does, so in-stock graphs keep a 0 floor.
  const computedYMin = useMemo(() => {
    if (!values.length) return 0;
    const trough = Math.min(...values, 0);
    if (trough >= 0) return 0;
    const magnitude = Math.abs(trough);
    const order = Math.pow(10, Math.floor(Math.log10(magnitude)));
    return -Math.ceil((magnitude * 1.15) / order) * order;
  }, [values]);

  const computedYStep = useMemo(() => {
    if (yStep !== undefined) return yStep;
    return Math.max(1, Math.round((computedYMax - computedYMin) / 5));
  }, [computedYMax, computedYMin, yStep]);

  const data = {
    labels,
    datasets: [
      {
        label: 'Inventory',
        data: values,
        fill: false,
        borderColor: '#2383e2',
        tension: 0.2,
        pointHitRadius: 5,
        pointHoverRadius: 5,
        pointBackgroundColor: '#2383e2',
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(32,32,32,0.95)',
        titleFont: { family: 'Inter', size: 11, weight: '600' },
        bodyFont: { family: 'Inter', size: 11 },
        padding: 8,
        borderColor: 'rgba(255,255,255,0.08)',
        borderWidth: 1,
        cornerRadius: 6,
        displayColors: false,
        callbacks: {
          title: (items) => items?.[0]?.label || '',
          label: (item) => `Qty: ${item.parsed.y}`,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
    scales: {
      x: {
        ticks: {
          callback: () => '',
          font: { family: 'Inter' },
        },
        grid: { display: false },
      },
      y: {
        min: computedYMin,
        max: computedYMax,
        ticks: { stepSize: computedYStep, font: { family: 'Inter', size: 10 } },
        grid: { color: 'rgba(0,0,0,0.04)' },
      },
    },
  };

  const handleClick = () => {
    if (productId) navigate(`/product/${productId}`);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        width: '100%',
        cursor: 'pointer',
        '&:hover': { opacity: 0.85 },
        transition: 'opacity 0.15s',
      }}
    >
      {productTitle && (
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            mb: 1,
            fontSize: '0.8rem',
            color: 'text.primary',
          }}
        >
          {productTitle}
        </Typography>
      )}
      {loaded ? (
        inventoryData.length === 0 ? (
          <Box sx={{
            height: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>No inventory data</Typography>
          </Box>
        ) : (
          <Line data={data} options={options} />
        )
      ) : (
        <Skeleton
          variant="rectangular"
          height={160}
          sx={{ borderRadius: 1 }}
          animation="wave"
        />
      )}
    </Box>
  );
}

export default React.memo(DashGraphs);
